import React from 'react';
import { Platform, type StyleProp, type TextStyle, type ViewStyle } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import type { Insets } from '@rn-primitives/types';
import {
  NativeSelectScrollView,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectSeparator,
  EMPTY_SELECT_VALUE,
} from '@/components/ui/Select';
import { useTheme } from '@/lib/styling';
import { Theme } from '@/theme';

type Option = {
  value: string;
  label: string;
  disabled?: boolean;
};

type AppSelectProps = {
  value?: string;
  onChange: (value: string) => void;
  options: Option[];
  placeholder?: string;
  width?: number;
  disabled?: boolean;
  triggerStyle?: StyleProp<ViewStyle>;
  valueStyle?: StyleProp<TextStyle>;
  contentStyle?: StyleProp<ViewStyle>;
  contentInsets?: Insets;
  portalHost?: string;
  simplifiedTrigger?: boolean;
};

export function AppSelect({
  value,
  onChange,
  options,
  placeholder = 'Select option',
  width,
  disabled,
  triggerStyle,
  valueStyle,
  contentStyle,
  contentInsets,
  portalHost,
  simplifiedTrigger = false,
}: AppSelectProps) {
  const insets = useSafeAreaInsets();
  const theme = useTheme<Theme>();
  const [triggerWidth, setTriggerWidth] = React.useState<number | undefined>(undefined);

  const bottomInset =
    contentInsets?.bottom ??
    Platform.select({
      ios: insets.bottom,
      android: insets.bottom + 24,
      default: insets.bottom,
    });

  const resolvedContentInsets: Insets = contentInsets ?? {
    top: insets.top,
    left: 12,
    right: 12,
    ...(bottomInset === undefined ? {} : { bottom: bottomInset }),
  };

  const widthStyle = width ? { width } : undefined;
  
  const triggerSurfaceStyle: ViewStyle = simplifiedTrigger
    ? {
        alignItems: 'center',
        backgroundColor: 'transparent',
        borderColor: 'transparent',
        borderRadius: theme.borderRadii.none,
        borderWidth: 0,
        elevation: 0,
        flexDirection: 'row',
        height: 48,
        justifyContent: 'space-between',
        paddingHorizontal: 0,
        width: '100%',
        shadowColor: 'transparent',
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0,
        shadowRadius: 0,
      }
    : {
        alignItems: 'center',
        backgroundColor: theme.colors.surface,
        borderColor: theme.colors.border,
        borderRadius: theme.borderRadii.m,
        borderWidth: 1,
        elevation: 0,
        flexDirection: 'row',
        height: 40,
        justifyContent: 'space-between',
        paddingHorizontal: theme.spacing.m,
        shadowColor: 'transparent',
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 0,
        shadowRadius: 0,
      };

  const triggerStyleFlatten = StyleSheet.flatten([
    triggerSurfaceStyle,
    widthStyle,
    triggerStyle,
  ]) as ViewStyle;
  
  const contentWidth =
    width ??
    (typeof triggerStyleFlatten?.width === 'number' ? triggerStyleFlatten.width : undefined) ??
    triggerWidth ??
    (typeof triggerStyleFlatten?.minWidth === 'number' ? triggerStyleFlatten.minWidth : undefined);
  
  const contentWidthStyle = contentWidth ? { width: contentWidth } : undefined;
  
  const contentSurfaceStyle: ViewStyle = {
    backgroundColor: theme.colors.surface,
    borderColor: theme.colors.border,
    borderWidth: 1,
    borderRadius: theme.borderRadii.m,
    shadowColor: 'transparent',
    shadowOpacity: 0,
    shadowRadius: 0,
    shadowOffset: { width: 0, height: 0 },
    elevation: 2,
  };

  const mergedContentStyle = StyleSheet.flatten([
    contentSurfaceStyle,
    contentWidthStyle,
    contentStyle,
  ]);
  
  const mergedValueStyle = StyleSheet.flatten([
    {
      color: simplifiedTrigger ? theme.colors.press : undefined,
      flex: 1,
      flexShrink: 1,
      minWidth: 0,
    },
    simplifiedTrigger && {
      fontFamily: theme.typography.fontFamily.display,
      fontSize: 24,
      fontWeight: '900',
      includeFontPadding: false,
      letterSpacing: -0.5,
      lineHeight: 30,
    },
    valueStyle,
  ]) as TextStyle;
  
  const selectProps = disabled === undefined ? {} : { disabled };
  const normalizedValue = value === '' ? EMPTY_SELECT_VALUE : value;
  const selectValueProps = normalizedValue === undefined ? {} : { value: normalizedValue };
  
  const normalizedOptions = options.map((option) => ({
    ...option,
    value: option.value === '' ? EMPTY_SELECT_VALUE : option.value,
  }));

  return (
    <Select
      onValueChange={(nextValue) => onChange(nextValue === EMPTY_SELECT_VALUE ? '' : nextValue)}
      options={normalizedOptions.map((option) => ({
        value: option.value,
        label: option.label,
      }))}
      {...selectValueProps}
      {...selectProps}
    >
      <SelectTrigger
        surfaceStyle={[triggerSurfaceStyle, widthStyle]}
        style={triggerStyle}
        onLayout={(event) => {
          setTriggerWidth(event.nativeEvent.layout.width);
        }}
      >
        <SelectValue
          placeholder={placeholder}
          style={mergedValueStyle}
          numberOfLines={1}
        />
      </SelectTrigger>

      <SelectContent
        insets={resolvedContentInsets}
        style={mergedContentStyle}
        {...(portalHost ? { portalHost } : {})}
      >
        <NativeSelectScrollView style={[{ backgroundColor: theme.colors.surface }]}>
          <SelectGroup>
            {normalizedOptions.map((option, index) => (
              <React.Fragment key={option.value}>
                {index > 0 && <SelectSeparator />}
                <SelectItem
                  value={option.value}
                  label={option.label}
                  disabled={option.disabled}
                >
                  {option.label}
                </SelectItem>
              </React.Fragment>
            ))}
          </SelectGroup>
        </NativeSelectScrollView>
      </SelectContent>
    </Select>
  );
}
